// Spades subgame for Classic Card Games.
// 4 players in 2 fixed teams (seats 0&2 vs 1&3). Each player bids before play.
// Spades are permanent trump. Make your bid or lose 10× it. Extra tricks are bags.
// Nil bid: take 0 tricks for +nilBonus; fail for −nilBonus. First team to 500 wins.

import { shuffleDeck, cardRank, cardSuit, cardName } from './cards';

const SPADES = 0;

function isSpade(card: number): boolean { return cardSuit(card) === SPADES; }
// Trick-winning value: A=13, K=12, Q=11, J=10, 10=9, …, 3=2, 2=1
function trickRank(rank: number): number { return rank === 0 ? 13 : rank; }

// ---- Types ----

export interface SpadesConfig {
  targetScore: number;  // first team to reach this wins (default 500)
  nilBonus: number;     // points for successful nil bid (default 100)
  bagPenalty: number;   // penalty per 10 cumulative bags (default 100)
}
export const DEFAULT_SPADES_CONFIG: SpadesConfig = { targetScore: 500, nilBonus: 100, bagPenalty: 100 };

export interface PlayerInit { id: string; name: string; isBot?: boolean; }

export type SpadesPhase = 'bidding' | 'playing' | 'roundOver' | 'complete';
export type SpadesActionType = 'bid' | 'play' | 'nextRound';

export interface SpadesPlayer {
  id: string;
  name: string;
  hand: number[];
  bid: number | null;  // null = not yet bid this round
  isNilBid: boolean;   // true if the player intentionally bid 0
  tricksWon: number;
  isBot: boolean;
  teamIndex: 0 | 1;   // seats 0&2 = team 0; seats 1&3 = team 1
}

export interface SpadesTeam {
  teamIndex: 0 | 1;
  playerIndices: [number, number];
  totalScore: number;
  bags: number;  // cumulative bags across all rounds
}

export interface TrickCard { playerId: string; card: number; }

export interface SpadesRoundResult {
  teams: Array<{
    teamIndex: 0 | 1;
    nonNilBid: number;
    tricksWon: number;
    madeIt: boolean;
    roundPoints: number;
    bagsAdded: number;
    bagPenalty: number;
    nils: Array<{ playerId: string; playerName: string; success: boolean; points: number }>;
  }>;
}

export interface SpadesState {
  config: SpadesConfig;
  phase: SpadesPhase;
  roundNumber: number;
  dealerIndex: number;
  biddingIndex: number;  // player index whose turn it is to bid
  players: SpadesPlayer[];
  teams: [SpadesTeam, SpadesTeam];
  currentTrick: TrickCard[];
  trickLeaderIndex: number;
  spadesBroken: boolean;
  tricksPlayed: number;
  roundResult: SpadesRoundResult | null;
  rng: number;
  winnerId: string | null;      // first player of winning team (for dispatcher compat)
  winningTeam: 0 | 1 | null;
}

export type SpadesAction =
  | { type: 'bid'; playerId: string; amount: number }
  | { type: 'play'; playerId: string; card: number }
  | { type: 'nextRound' };

export interface SpadesView extends SpadesState { viewPlayerId: string | null; }

// ---- Core API ----

export function createGame(config: SpadesConfig, players: PlayerInit[], seed = Date.now()): SpadesState {
  const all = [...players];
  while (all.length < 4) all.push({ id: `bot-fill-${all.length}`, name: `Bot ${all.length + 1}`, isBot: true });
  const four = all.slice(0, 4);

  const base: SpadesState = {
    config,
    phase: 'bidding',
    roundNumber: 0,
    dealerIndex: 3,       // first bidder/leader = (3+1)%4 = 0
    biddingIndex: 0,
    players: four.map((p, i) => ({
      id: p.id, name: p.name, hand: [],
      bid: null, isNilBid: false, tricksWon: 0,
      isBot: p.isBot ?? false,
      teamIndex: (i % 2) as 0 | 1,
    })),
    teams: [
      { teamIndex: 0, playerIndices: [0, 2], totalScore: 0, bags: 0 },
      { teamIndex: 1, playerIndices: [1, 3], totalScore: 0, bags: 0 },
    ],
    currentTrick: [],
    trickLeaderIndex: 0,
    spadesBroken: false,
    tricksPlayed: 0,
    roundResult: null,
    rng: seed | 0,
    winnerId: null,
    winningTeam: null,
  };
  return startRound(base);
}

export function applyAction(state: SpadesState, action: SpadesAction): SpadesState {
  switch (action.type) {
    case 'bid':      return applyBid(state, action.playerId, action.amount);
    case 'play':     return applyPlay(state, action.playerId, action.card);
    case 'nextRound': return state.phase === 'roundOver' ? startRound(state) : state;
  }
}

export function getPlayerView(state: SpadesState, playerId: string | null): SpadesView {
  const players = state.players.map(p => ({
    ...p,
    hand: playerId !== null && p.id !== playerId ? [] : p.hand,
  }));
  return { ...state, players, viewPlayerId: playerId };
}

export function describe(config: SpadesConfig): string {
  return [
    'Spades — 4 Players, 2 Teams',
    '',
    'Partners sit opposite each other: the 1st and 3rd players are Team A, the 2nd and 4th Team B.',
    'Before play, each player bids the number of tricks they expect to win (0–13).',
    '  Nil bid (0): take exactly 0 tricks for +' + config.nilBonus + '; take any tricks for −' + config.nilBonus + '.',
    '',
    'Play 13 tricks. Spades are always trump and beat all other suits.',
    '  • Must follow suit if possible.',
    '  • Spades cannot be led until "broken" (a spade was played as a discard).',
    '  • When void in led suit, any card including spades may be played.',
    '',
    'Scoring per round (per team):',
    '  • Make your bid: +10 × bid. Extra tricks ("bags") each add 1 point.',
    '  • Miss your bid: −10 × bid.',
    '  • Bags accumulate; every 10 bags = −' + config.bagPenalty + ' penalty.',
    '',
    `Game ends when a team reaches ${config.targetScore} points. Highest score wins.`,
  ].join('\n');
}

export function getValidActions(state: SpadesState, playerId: string): SpadesActionType[] {
  if (state.phase === 'bidding') {
    return state.players[state.biddingIndex]?.id === playerId ? ['bid'] : [];
  }
  if (state.phase === 'playing') {
    const actorIdx = (state.trickLeaderIndex + state.currentTrick.length) % 4;
    return state.players[actorIdx]?.id === playerId ? ['play'] : [];
  }
  if (state.phase === 'roundOver') return ['nextRound'];
  return [];
}

export function getBotAction(playerId: string, state: SpadesState): SpadesAction {
  const playerIdx = state.players.findIndex(p => p.id === playerId);
  const player = state.players[playerIdx];

  if (state.phase === 'bidding') {
    return { type: 'bid', playerId, amount: estimateBid(player, state) };
  }
  if (state.phase === 'playing') {
    return { type: 'play', playerId, card: choosePlay(state, player) };
  }
  return { type: 'nextRound' };
}

export function validCardsForPlayer(state: SpadesState, playerId: string): number[] {
  if (state.phase !== 'playing') return [];
  const actorIdx = (state.trickLeaderIndex + state.currentTrick.length) % 4;
  if (state.players[actorIdx]?.id !== playerId) return [];
  return validCards(state, state.players[actorIdx]);
}

// ---- Game flow ----

function startRound(state: SpadesState): SpadesState {
  const [deck, rng] = shuffleDeck(state.rng);
  const hands: number[][] = [[], [], [], []];
  for (let i = 0; i < 52; i++) hands[i % 4].push(deck[i]);

  const dealerIndex = (state.dealerIndex + 1) % 4;
  const firstBidder = (dealerIndex + 1) % 4;

  const players = state.players.map((p, i) => ({
    ...p,
    hand: [...hands[i]].sort((a, b) => cardSuit(a) - cardSuit(b) || trickRank(cardRank(a)) - trickRank(cardRank(b))),
    bid: null,
    isNilBid: false,
    tricksWon: 0,
  }));

  return {
    ...state,
    phase: 'bidding',
    roundNumber: state.roundNumber + 1,
    dealerIndex,
    biddingIndex: firstBidder,
    players,
    teams: state.teams.map(t => ({ ...t })) as [SpadesTeam, SpadesTeam],
    currentTrick: [],
    trickLeaderIndex: firstBidder,
    spadesBroken: false,
    tricksPlayed: 0,
    roundResult: null,
    rng,
  };
}

function applyBid(state: SpadesState, playerId: string, amount: number): SpadesState {
  if (state.phase !== 'bidding' || amount < 0 || amount > 13) return state;
  const pIdx = state.players.findIndex(p => p.id === playerId);
  if (pIdx !== state.biddingIndex || state.players[pIdx].bid !== null) return state;

  const players = state.players.map((p, i) =>
    i === pIdx ? { ...p, bid: amount, isNilBid: amount === 0 } : p
  );
  const nextBidder = (state.biddingIndex + 1) % 4;
  const allBid = players.every(p => p.bid !== null);

  if (!allBid) return { ...state, players, biddingIndex: nextBidder };
  return { ...state, phase: 'playing', players, biddingIndex: nextBidder };
}

function applyPlay(state: SpadesState, playerId: string, card: number): SpadesState {
  if (state.phase !== 'playing') return state;
  const actorIdx = (state.trickLeaderIndex + state.currentTrick.length) % 4;
  const actor = state.players[actorIdx];
  if (actor.id !== playerId || !actor.hand.includes(card)) return state;
  if (!validCards(state, actor).includes(card)) return state;

  const newSpadesBroken = state.spadesBroken || isSpade(card);
  const trick = [...state.currentTrick, { playerId, card }];
  const players = state.players.map((p, i) =>
    i === actorIdx ? { ...p, hand: p.hand.filter(c => c !== card) } : p
  );

  if (trick.length < 4) {
    return { ...state, currentTrick: trick, players, spadesBroken: newSpadesBroken };
  }
  return resolveTrick({ ...state, currentTrick: trick, players, spadesBroken: newSpadesBroken });
}

function resolveTrick(state: SpadesState): SpadesState {
  const { currentTrick } = state;
  const ledSuit = cardSuit(currentTrick[0].card);
  const hasSpade = currentTrick.some(tc => isSpade(tc.card));

  let winIdx = 0, winTV = 0;
  if (hasSpade) {
    for (let i = 0; i < 4; i++) {
      const { card } = currentTrick[i];
      if (isSpade(card) && trickRank(cardRank(card)) > winTV) {
        winTV = trickRank(cardRank(card));
        winIdx = i;
      }
    }
  } else {
    winTV = trickRank(cardRank(currentTrick[0].card));
    for (let i = 1; i < 4; i++) {
      const { card } = currentTrick[i];
      if (cardSuit(card) === ledSuit && trickRank(cardRank(card)) > winTV) {
        winTV = trickRank(cardRank(card));
        winIdx = i;
      }
    }
  }

  const winnerPlayerId = currentTrick[winIdx].playerId;
  const winnerIdx = state.players.findIndex(p => p.id === winnerPlayerId);
  const players = state.players.map((p, i) =>
    i === winnerIdx ? { ...p, tricksWon: p.tricksWon + 1 } : p
  );
  const tricksPlayed = state.tricksPlayed + 1;

  if (tricksPlayed < 13) {
    return { ...state, players, currentTrick: [], trickLeaderIndex: winnerIdx, tricksPlayed };
  }
  return endRound({ ...state, players, currentTrick: [], trickLeaderIndex: winnerIdx, tricksPlayed });
}

function endRound(state: SpadesState): SpadesState {
  const updatedTeams = state.teams.map(t => ({ ...t })) as [SpadesTeam, SpadesTeam];
  const teamResults: SpadesRoundResult['teams'] = [];

  for (const team of state.teams) {
    const teamPlayers = team.playerIndices.map(i => state.players[i]);
    const nilPlayers = teamPlayers.filter(p => p.isNilBid);
    const nonNilPlayers = teamPlayers.filter(p => !p.isNilBid);

    const nonNilBid = nonNilPlayers.reduce((s, p) => s + (p.bid ?? 0), 0);
    const allTricks = teamPlayers.reduce((s, p) => s + p.tricksWon, 0);

    // Nil scoring
    const nils = nilPlayers.map(p => {
      const success = p.tricksWon === 0;
      return { playerId: p.id, playerName: p.name, success, points: success ? state.config.nilBonus : -state.config.nilBonus };
    });
    const nilPoints = nils.reduce((s, n) => s + n.points, 0);

    // Non-nil scoring: team tricks include all tricks (failed nil's tricks become bags)
    let roundPoints = nilPoints;
    let bagsAdded = 0;
    const madeIt = allTricks >= nonNilBid;
    if (madeIt) {
      roundPoints += 10 * nonNilBid;
      bagsAdded = allTricks - nonNilBid;
    } else {
      roundPoints -= 10 * nonNilBid;
    }

    // Bag penalty: check if crossing a multiple of 10
    const oldBags = updatedTeams[team.teamIndex].bags;
    const newBags = oldBags + bagsAdded;
    const penalties = Math.floor(newBags / 10) - Math.floor(oldBags / 10);
    const bagPenalty = penalties * state.config.bagPenalty;
    roundPoints -= bagPenalty;

    updatedTeams[team.teamIndex].totalScore += roundPoints;
    updatedTeams[team.teamIndex].bags = newBags;

    teamResults.push({ teamIndex: team.teamIndex, nonNilBid, tricksWon: allTricks, madeIt, roundPoints, bagsAdded, bagPenalty, nils });
  }

  const roundResult: SpadesRoundResult = { teams: teamResults };
  const gameOver = updatedTeams.some(t => t.totalScore >= state.config.targetScore);

  if (!gameOver) {
    return { ...state, phase: 'roundOver', teams: updatedTeams, roundResult, winnerId: null, winningTeam: null };
  }

  const winningTeam: 0 | 1 = updatedTeams[0].totalScore >= updatedTeams[1].totalScore ? 0 : 1;
  const winnerId = state.players[state.teams[winningTeam].playerIndices[0]].id;
  return { ...state, phase: 'complete', teams: updatedTeams, roundResult, winnerId, winningTeam };
}

// ---- Valid cards ----

function validCards(state: SpadesState, player: SpadesPlayer): number[] {
  const { hand } = player;
  const isLeading = state.currentTrick.length === 0;

  if (isLeading) {
    if (!state.spadesBroken) {
      const nonSpades = hand.filter(c => !isSpade(c));
      if (nonSpades.length > 0) return nonSpades;
    }
    return hand;
  }

  const ledSuit = cardSuit(state.currentTrick[0].card);
  const following = hand.filter(c => cardSuit(c) === ledSuit);
  return following.length > 0 ? following : hand;
}

// ---- Bot: bidding ----

function estimateBid(player: SpadesPlayer, state: SpadesState): number {
  const hand = player.hand;
  let estimate = 0;

  const suitCounts = [0, 0, 0, 0];
  for (const c of hand) suitCounts[cardSuit(c)]++;

  for (const card of hand) {
    const tv = trickRank(cardRank(card));
    if (isSpade(card)) {
      // Spades are trump — value by how likely to win
      if (tv === 13) estimate += 1.0;
      else if (tv === 12) estimate += 0.85;
      else if (tv === 11) estimate += 0.65;
      else if (tv === 10) estimate += 0.45;
      else if (tv >= 7)  estimate += 0.25;
      else               estimate += 0.1;
    } else {
      // Non-trump: can be trumped, so discount
      if (tv === 13) estimate += 0.7;
      else if (tv === 12) estimate += 0.4;
      else if (tv === 11) estimate += 0.2;
      else               estimate += 0.05;
    }
  }

  // Void bonus: each non-spade void means you can trump
  for (let s = 1; s <= 3; s++) {
    if (suitCounts[s] === 0) estimate += 1.0;
    else if (suitCounts[s] === 1) estimate += 0.4;
  }

  // Nil bid: if hand is weak enough and no high spades
  const hasHighSpade = hand.some(c => isSpade(c) && trickRank(cardRank(c)) >= 12);
  if (estimate < 1.5 && !hasHighSpade) return 0;

  return Math.max(1, Math.round(estimate));
}

// ---- Bot: playing ----

function choosePlay(state: SpadesState, player: SpadesPlayer): number {
  const valid = validCards(state, player);
  if (valid.length === 1) return valid[0];

  const partner = state.players.find(p => p.teamIndex === player.teamIndex && p.id !== player.id)!;
  const trickWin = state.currentTrick.length > 0 ? trickWinner(state.currentTrick) : null;
  const partnerWinning = trickWin?.playerId === partner.id;
  const myTeamNeeds = teamTricksNeeded(state, player.teamIndex);
  const oppTeamNeeds = teamTricksNeeded(state, (1 - player.teamIndex) as 0 | 1);
  const isLeading = state.currentTrick.length === 0;

  // Nil bidder: avoid winning at all costs
  if (player.isNilBid) return chooseNilPlay(valid, trickWin, isLeading);

  // Partner is on nil: try to win every trick for them
  if (partner.isNilBid) return chooseNilSupport(valid, trickWin, partnerWinning, isLeading);

  if (isLeading) return chooseLead(valid, myTeamNeeds, oppTeamNeeds, state);
  return chooseFollow(valid, trickWin!, partnerWinning, myTeamNeeds);
}

function chooseNilPlay(valid: number[], trickWin: TrickCard | null, isLeading: boolean): number {
  if (isLeading) {
    const nonSpades = valid.filter(c => !isSpade(c));
    return lowest(nonSpades.length > 0 ? nonSpades : valid);
  }

  if (!trickWin) return lowest(valid);

  const winCard = trickWin.card;
  const winIsSpade = isSpade(winCard);
  const winTV = trickRank(cardRank(winCard));

  const allSameSuit = valid.every(c => cardSuit(c) === cardSuit(valid[0]));

  if (allSameSuit && !winIsSpade && cardSuit(valid[0]) === cardSuit(winCard)) {
    // Following same suit as winner — play highest card below it
    const canLose = valid.filter(c => trickRank(cardRank(c)) < winTV);
    if (canLose.length > 0) return highest(canLose);
    return lowest(valid); // all win, pick smallest
  }

  if (winIsSpade && !valid.some(isSpade)) {
    // Trick won by spade, we follow non-spade suit — all lose, dump highest
    return highest(valid);
  }

  // Void — discard without playing spades if possible
  const nonSpades = valid.filter(c => !isSpade(c));
  if (nonSpades.length > 0) return highest(nonSpades);
  return lowest(valid); // only spades, play lowest
}

function chooseNilSupport(valid: number[], trickWin: TrickCard | null, partnerWinning: boolean, isLeading: boolean): number {
  if (isLeading) {
    // Lead high winners to establish control before nil partner has to play
    const spades = valid.filter(isSpade);
    const aces = valid.filter(c => trickRank(cardRank(c)) === 13);
    if (aces.length > 0) return aces[0];
    if (spades.length > 0) return highest(spades);
    return highest(valid);
  }

  if (partnerWinning) {
    // Nil partner is winning — take it over with a higher card to protect them
    if (trickWin) {
      const canBeat = valid.filter(c => cardSuit(c) === cardSuit(trickWin.card) && trickRank(cardRank(c)) > trickRank(cardRank(trickWin.card)));
      if (canBeat.length > 0) return lowest(canBeat);
    }
  }

  if (!trickWin) return highest(valid);

  // Try to win the trick
  const winCard = trickWin.card;
  const inSuit = valid.filter(c => cardSuit(c) === cardSuit(winCard));
  if (inSuit.length > 0) {
    const canWin = inSuit.filter(c => trickRank(cardRank(c)) > trickRank(cardRank(winCard)));
    if (canWin.length > 0) return lowest(canWin);
    return highest(inSuit);
  }
  // Void — trump if possible
  const spades = valid.filter(isSpade);
  return spades.length > 0 ? lowest(spades) : highest(valid);
}

function chooseLead(valid: number[], myNeeds: number, oppNeeds: number, state: SpadesState): number {
  const spades = valid.filter(isSpade);
  const nonSpades = valid.filter(c => !isSpade(c));

  if (myNeeds > 0) {
    // Need tricks: lead winners first
    const aces = valid.filter(c => trickRank(cardRank(c)) === 13);
    if (aces.length > 0) return aces[0];
    const kings = valid.filter(c => trickRank(cardRank(c)) === 12);
    if (kings.length > 0) return kings[0];
    // Lead high spade to draw out trump
    if (spades.length > 0) return highest(spades);
    return highest(nonSpades.length > 0 ? nonSpades : valid);
  }

  // Bid made — avoid bags, but disrupt opponents if they're close to making their bid
  if (oppNeeds <= 1 && oppNeeds > 0) {
    // Lead a strong card to win this trick and set them
    if (spades.length > 0) return highest(spades);
    const aces = nonSpades.filter(c => trickRank(cardRank(c)) === 13);
    if (aces.length > 0) return aces[0];
  }

  // Lead lowest safe card to minimize bags
  if (nonSpades.length > 0) return lowest(nonSpades);
  return lowest(spades);
}

function chooseFollow(valid: number[], trickWin: TrickCard, partnerWinning: boolean, myNeeds: number): number {
  const winCard = trickWin.card;
  const winIsSpade = isSpade(winCard);
  const winTV = trickRank(cardRank(winCard));
  const isVoid = !valid.some(c => cardSuit(c) === cardSuit(winCard)) || winIsSpade;

  if (isVoid) {
    return chooseDiscard(valid, trickWin, partnerWinning, myNeeds);
  }

  // Following suit (and trick winner is in the same led suit)
  if (partnerWinning) {
    // Let partner have it — play highest card that falls below winner
    const canLose = valid.filter(c => trickRank(cardRank(c)) < winTV);
    if (canLose.length > 0) return highest(canLose);
    return lowest(valid); // forced to beat partner, minimize damage
  }

  if (myNeeds > 0) {
    // Win the trick with smallest winning card
    const canWin = valid.filter(c => trickRank(cardRank(c)) > winTV);
    if (canWin.length > 0) return lowest(canWin);
    return highest(valid); // can't win, signal strength at least
  }

  // Don't need tricks — duck
  const canLose = valid.filter(c => trickRank(cardRank(c)) < winTV);
  if (canLose.length > 0) return highest(canLose);
  return lowest(valid);
}

function chooseDiscard(valid: number[], trickWin: TrickCard, partnerWinning: boolean, myNeeds: number): number {
  const winCard = trickWin.card;
  const spades = valid.filter(isSpade);
  const nonSpades = valid.filter(c => !isSpade(c));

  if (partnerWinning && myNeeds <= 0) {
    // Partner has the trick and we don't need it — dump highest non-spade
    return nonSpades.length > 0 ? highest(nonSpades) : lowest(spades);
  }

  if (!partnerWinning && myNeeds > 0 && spades.length > 0) {
    // Trump the trick with spades (or over-trump if needed)
    if (isSpade(winCard)) {
      const higherSpades = spades.filter(c => trickRank(cardRank(c)) > trickRank(cardRank(winCard)));
      if (higherSpades.length > 0) return lowest(higherSpades);
      // Can't over-trump — dump garbage
      return nonSpades.length > 0 ? highest(nonSpades) : lowest(spades);
    }
    return lowest(spades); // trump a non-spade trick
  }

  // Don't trump partner or don't need tricks — dump highest non-spade
  if (nonSpades.length > 0) return highest(nonSpades);
  return lowest(spades);
}

// ---- Helpers ----

function teamTricksNeeded(state: SpadesState, teamIndex: 0 | 1): number {
  const team = state.teams[teamIndex];
  const teamPlayers = team.playerIndices.map(i => state.players[i]);
  const nonNilBid = teamPlayers.filter(p => !p.isNilBid).reduce((s, p) => s + (p.bid ?? 0), 0);
  const allTricks = teamPlayers.reduce((s, p) => s + p.tricksWon, 0);
  return Math.max(0, nonNilBid - allTricks);
}

function trickWinner(trick: TrickCard[]): TrickCard {
  const ledSuit = cardSuit(trick[0].card);
  const hasSpade = trick.some(tc => isSpade(tc.card));
  if (hasSpade) {
    return trick.reduce((best, tc) =>
      isSpade(tc.card) && trickRank(cardRank(tc.card)) > (isSpade(best.card) ? trickRank(cardRank(best.card)) : 0)
        ? tc : best
    );
  }
  return trick.reduce((best, tc) =>
    cardSuit(tc.card) === ledSuit && trickRank(cardRank(tc.card)) > trickRank(cardRank(best.card))
      ? tc : best
  );
}

function highest(cards: number[]): number {
  return cards.reduce((best, c) => trickRank(cardRank(c)) > trickRank(cardRank(best)) ? c : best);
}

function lowest(cards: number[]): number {
  return cards.reduce((best, c) => trickRank(cardRank(c)) < trickRank(cardRank(best)) ? c : best);
}

export { cardName, isSpade, trickRank };
